/**
 * Human Resources · access levels.
 *
 * Six levels, from the HR super admin, who can do everything, down to the
 * admin staff who keep attendance and details but never see pay. The page
 * shows what each level can do, who holds it, lets a super admin give
 * somebody a level, and lets anyone preview the suite as a lower level sees
 * it — the preview only hides; the platform decides what is really allowed.
 */
import { useState } from "react";
import { useHrAccessView, useHrSetAccess } from "../../api/queries";
import { useSession } from "../../app/SessionContext";
import { useScreenContext } from "../../assistant/screen";
import { Button, Card, Field, Select, Skeleton, Table } from "../../design/components";
import { HR_ABILITIES, HR_LEVELS, type HrLevelKey } from "../../domain/hrAccess";
import { t } from "../../i18n";
import { Page } from "../../layout/AppShell";
import { setViewAs, useHrAccess } from "./access";
import { Avatar, h, PreviewBar } from "./kit";
import { HrError, s, SUITE } from "./shared";

export function HrAccessPage() {
  const session = useSession();
  const access = useHrAccess();
  const q = useHrAccessView();
  const set = useHrSetAccess();
  const [who, setWho] = useState("");
  const [role, setRole] = useState("hr_assistant");
  const v = q.data;
  const levels = HR_LEVELS.filter((l) => l.key !== "none");
  const holdersOf = (roleKey: string | null) => v?.roles.find((r) => r.key === roleKey)?.holders ?? [];

  useScreenContext({
    route: "/hr-access",
    title: "Access levels",
    summary: [
      t("You hold the {level} level.", { level: t(access.granted.name) }),
      t("Six levels, from the HR super admin with full access down to admin staff who never see pay."),
    ],
    offers: [{ label: t("Who can see salaries?"), ask: "which HR levels can see salaries?" }],
    worksInFull: true,
  });

  return (
    <Page eyebrow={t("{site} · {suite}", { site: session.site.code, suite: t(SUITE) })} title="Access levels">
      <PreviewBar />
      <Card
        title={t("Your level")}
        sub={t("Set by the platform. Previews can only hide things.")}
      >
        <div className={s.filters}>
          <div>
            <div className={h.big} data-testid="hr-my-level">
              {t(access.granted.name)}
            </div>
            <div className={s.dim}>{t(access.granted.who)}</div>
          </div>
          <Field label="See the suite as">
            <Select
              value={access.previewing ? access.level.key : ""}
              onChange={(e) => setViewAs((e.target.value || null) as HrLevelKey | null)}
              data-testid="hr-view-as"
            >
              <option value="">{t("Myself")}</option>
              {HR_LEVELS.filter((l) => l.key !== access.granted.key).map((l) => (
                <option key={l.key} value={l.key}>
                  {t(l.name)}
                </option>
              ))}
            </Select>
          </Field>
        </div>
      </Card>

      <div className={h.levels}>
        {levels.map((l, i) => (
          <section
            key={l.key}
            className={h.level}
            data-current={access.granted.key === l.key}
            data-testid={`hr-level-${l.key}`}
          >
            <h3>
              {i + 1}. {t(l.name)}
            </h3>
            <div className={s.dim}>{t(l.who)}</div>
            <ul className={h.list}>
              {HR_ABILITIES.map((a) => (
                <li key={a.key} style={{ display: "flex", gap: 8, fontSize: 13 }}>
                  <span className={l.keys.includes(a.key) ? h.tick : h.cross}>
                    {l.keys.includes(a.key) ? "✓" : "—"}
                  </span>
                  <span className={l.keys.includes(a.key) ? undefined : s.dim}>{t(a.label)}</span>
                </li>
              ))}
            </ul>
            {l.role && l.key !== "executive" && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {holdersOf(l.role).length === 0 ? (
                  <span className={s.dim}>{t("Nobody holds this level yet.")}</span>
                ) : (
                  holdersOf(l.role).map((p) => (
                    <span
                      key={p.id}
                      title={p.email ?? undefined}
                      style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13 }}
                    >
                      <Avatar name={p.displayName} size={24} />
                      {p.displayName}
                    </span>
                  ))
                )}
              </div>
            )}
          </section>
        ))}
      </div>

      {q.isLoading && <Skeleton rows={3} />}
      {q.error && <HrError error={q.error} retry={() => void q.refetch()} />}
      {v && (
        <Card
          title={t("Give somebody an HR level")}
          sub={
            v.canAssign && access.can.admin
              ? t("One level per person. A new one replaces the old.")
              : t("Only the HR super admin gives levels.")
          }
        >
          {v.canAssign && access.can.admin ? (
            <form
              className={s.form}
              onSubmit={(e) => {
                e.preventDefault();
                if (who) set.mutate({ principalId: who, role: role || null });
              }}
            >
              <Field label="Person (their platform sign-in)">
                <Select value={who} onChange={(e) => setWho(e.target.value)} data-testid="hr-access-who">
                  <option value="">{t("Choose a person")}</option>
                  {v.users.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.displayName}
                      {u.email ? ` · ${u.email}` : ""}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Level">
                <Select value={role} onChange={(e) => setRole(e.target.value)} data-testid="hr-access-role">
                  {HR_LEVELS.filter((l) => l.role && l.key !== "executive").map((l) => (
                    <option key={l.key} value={l.role as string}>
                      {t(l.name)}
                    </option>
                  ))}
                  <option value="">{t("No HR access")}</option>
                </Select>
              </Field>
              <Button type="submit" variant="primary" disabled={!who || set.isPending} data-testid="hr-access-save">
                {t("Give the level")}
              </Button>
            </form>
          ) : null}
          {set.error && <p className={s.err}>{set.error instanceof Error ? set.error.message : String(set.error)}</p>}
          {set.isSuccess && <p className={s.ok}>{t("Done. It takes effect at their next sign-in.")}</p>}
        </Card>
      )}

      <Card title={t("Who can do what")} collapsible>
        <Table>
          <thead>
            <tr>
              <th>{t("Ability")}</th>
              {levels.map((l) => (
                <th key={l.key}>{t(l.name)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {HR_ABILITIES.map((a) => (
              <tr key={a.key}>
                <td>{t(a.label)}</td>
                {levels.map((l) => (
                  <td key={l.key} className={l.keys.includes(a.key) ? h.tick : h.cross} style={{ textAlign: "center" }}>
                    {l.keys.includes(a.key) ? "✓" : "—"}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </Table>
        <p className={s.note}>
          {t(
            "The platform admin has full HR access. The accountable manager and project sponsor can view staff and costs.",
          )}
        </p>
      </Card>
    </Page>
  );
}
